import AuthLayout, { AlertType, AuthProps, IAlertProps, IInputError } from '@/components/layout/auth-layout'
import { clearAllCachedDiagnosticCenterIds } from '@/utils/diagnosticCenterCache'
import GuestLayout from '@/components/layout/guest-layout'
import AppleStoreButton from '@/components/shared/apple-store-button'
import AuthInput from '@/components/shared/auth-input'
import GooglePlayButton from '@/components/shared/google-play-button'
import Loading from '@/components/shared/loading'
import { useAuthContext } from '@/context/auth-context'
import { useAuth } from '@/hooks/use-auth'
import { handleError } from '@/libraries/util'
import { LoadingButton } from '@mui/lab'
import { Button, Box as Stack, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import { useRouter } from 'next/router'
import { ChangeEvent, FormEvent, MouseEvent, useEffect, useState } from 'react'
import { isAndroid, isIOS } from 'react-device-detect'
import QRCode from 'react-qr-code'

function LoginPage() {
  const router = useRouter()
  const { signIn } = useAuthContext()
  const [busy, setBusy] = useState(false)
  const [alert, setAlert] = useState<IAlertProps | null>(null)
  const [usernameError, setUsernameError] = useState<IInputError | null>(null)
  const siteKey = String(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '')

  const [values, setValues] = useState<AuthProps>({
    email: '',
    password: '',
    confirmPassword: '',
    showPassword: false,
  })

  const [publicLink, setPublicLink] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState<string | null>(null)
  const [captchaReady, setCaptchaReady] = useState(false)

  useEffect(() => {
    if (router.query) {
      const origin = typeof window !== 'undefined' && window.location.origin ? window.location.origin : ''
      setPublicLink(`${origin}${router.asPath}`)

      const { email } = router.query
      if (email) {
        setValues({ ...values, email: email.toString() })
      }

      // Landed here from the invite email's "Accept invitation" button (GET /v1/patient-invites/accept).
      const { invite, center } = router.query
      if (invite) {
        const centerName = center ? center.toString() : 'your care provider'
        if (invite === 'accepted' || invite === 'already_accepted') {
          // Uploads must now go to the accepted center, not a center cached before accepting.
          clearAllCachedDiagnosticCenterIds()
          setAlert({
            type: AlertType.success,
            message:
              invite === 'accepted'
                ? `You're now linked to ${centerName}. Your CT scans and reports — including ones you've already uploaded — are now shown in their account. Sign in to continue.`
                : `You're already connected to ${centerName}. Sign in to continue.`,
          })
        } else if (invite === 'expired') {
          setAlert({
            type: AlertType.warning,
            message: 'This invitation link has expired. Sign in and accept the invitation from your account, or ask your provider to resend it.',
          })
        } else {
          setAlert({
            type: AlertType.error,
            message: 'This invitation link is not valid. Sign in and accept the invitation from your account, or ask your provider to resend it.',
          })
        }
      }
    }
  }, [router.query])

  // useEffect(() => {
  //   if (values.email && values.password) {
  //     setCaptchaReady(true)
  //   } else {
  //     setCaptchaReady(false)
  //     setTurnstileToken(null) // reset token if fields are cleared
  //   }
  // }, [values.email, values.password])

  const handleChange = (prop: keyof AuthProps) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value })
  }

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    })
  }

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // if (!turnstileToken) {
    //   setTurnstileError('CAPTCHA verification required')
    //   return
    // }
    try {
      setBusy(true)
      setAlert(null)
      setUsernameError(null)
      await signIn(values.email, values.password)
      const { redirect } = router.query
      if (redirect) {
        router.replace(redirect.toString())
      } else {
        router.replace('/dashboard/')
      }
    } catch (error) {
      handleError(error, (message) => {
        setUsernameError({ message })
      })
    } finally {
      setBusy(false)
    }
  }

  const onCreateAccountBtnClick = () => {
    router.push('/register/').then()
  }

  return (
    <GuestLayout title={'Sign In'}>
      <AuthLayout alert={alert} title={'Hi, Welcome!'} description={'Please sign in to continue.'}>
        <Box component="form" onSubmit={handleSubmit}>
          <AuthInput
            required
            id={'email'}
            name={'email'}
            value={values.email}
            placeholder={'Enter your email address here'}
            type={'email'}
            title={'Email'}
            helperText={''}
            error={usernameError}
            onChange={handleChange('email')}
            showInputAdornment={false}
            onHandleClickInputAdornment={null}
            onHandleMouseInputAdornment={null}
          />

          <Grid container direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 1.2 }}>
            <AuthInput
              required
              id={'password'}
              name={'password'}
              value={values.password}
              placeholder={'Enter your password here'}
              type={values.showPassword ? 'text' : 'password'}
              title={'Password'}
              helperText={''}
              onChange={handleChange('password')}
              showInputAdornment={values.showPassword}
              onHandleClickInputAdornment={handleClickShowPassword}
              onHandleMouseInputAdornment={handleMouseDownPassword}
            />
          </Grid>
          <Grid container direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }}>
            <Link underline={'hover'} href={'/auth/forgot-password'}>
              <Typography
                component="small"
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: 'neutral.700',
                }}
              >
                Forgot password?
              </Typography>
            </Link>
          </Grid>

          <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
            {/* {captchaReady && (
              <Box mt={2} display="flex" justifyContent="center">
                <TurnstileWidget
                  siteKey={siteKey}
                  onSuccess={(token) => {
                    setTurnstileToken(token)
                    setTurnstileError(null)
                  }}
                  onError={(err) => {
                    setTurnstileError(err)
                    setTurnstileToken(null)
                  }}
                />
              </Box>
            )} */}

            {/* {turnstileError && (
              <Typography color="error" fontSize={12} mt={1}>
                CAPTCHA Error: {turnstileError}
              </Typography>
            )} */}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={busy}
              sx={{ height: 44, mt: 2 }}
            >
              Sign In
            </Button>

            <Typography component="small" marginY={1} color={'natural.900'}>
              Or
            </Typography>

            <LoadingButton
              loading={busy}
              onClick={() => onCreateAccountBtnClick()}
              fullWidth
              variant="outlined"
              sx={{ height: 44 }}
            >
              Create account
            </LoadingButton>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {!isIOS && !isAndroid && (
                <Grid
                  maxWidth={300}
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  alignContent={'center'}
                  my={2}
                  border={1}
                  p={2}
                  borderRadius={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    left: '0px',
                    top: '0px',
                    background: 'white',
                    border: '0.848px solid',
                    borderColor: 'neutral.200',
                    borderRadius: '5.936px',
                  }}
                >
                  {!(isAndroid || isIOS) && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        width: '90px',
                        height: '90px',
                        left: '0px',
                        top: '1px',
                        background: 'white',
                        border: '0.848px solid',
                        borderColor: 'neutral.200',
                        borderRadius: '5.936px',
                      }}
                    >
                      <div style={{ height: '50', margin: '5 5 5 5', maxWidth: 75, width: 75 }}>
                        {publicLink && (
                          <QRCode
                            size={256}
                            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                            value={publicLink}
                            viewBox={'0 0 256 256'}
                          />
                        )}
                        {!publicLink && <Loading />}
                      </div>
                    </Box>
                  )}
                  <Typography marginY={3} variant={'caption'}>
                    Scan this QR code in your phone to download{' '}
                    <Typography variant={'caption'} color={'primary.dark'} sx={{ color: 'primary.dark' }}>
                      HeartLung Health
                    </Typography>{' '}
                    app
                  </Typography>
                  <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ mt: 0.5 }}>
                    <Stack>
                      <GooglePlayButton />
                      <AppleStoreButton />
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>
        </Box>
      </AuthLayout>
    </GuestLayout>
  )
}

export default useAuth({
  Component: LoginPage,
  middleware: 'guest',
  redirectIfAuthenticated: '/dashboard/',
})
